/**
 * Micro-renderers used by the type-aware renderer.
 *
 * Each component handles one display flavour (email chip, currency, datetime,
 * etc.). They're intentionally small and unstyled where possible so the parent
 * renderer can compose them into rows or chips without fighting layout.
 *
 * No external dependencies beyond lucide-react icons.
 */

import { useState } from 'react'
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react'
import { Mail, Link as LinkIcon, Hash, Check, X, Copy, AlertTriangle, MapPin } from 'lucide-react'

import { normalizeArtifacts } from './normalizeArtifacts'
import { useCardTranslatedText } from '../../CardTranslation'

// ---------------------------------------------------------------------------
// Encoding-artifact badge
// ---------------------------------------------------------------------------

/**
 * Small inline indicator shown when displayed text had literal encoding
 * artifacts (e.g. `’`, mojibake) that were auto-corrected for display.
 * The backend applies the identical repair before posting, so the operator is
 * seeing exactly what will be sent — this badge simply flags that the raw
 * payload was not clean.
 */
export function ArtifactBadge({ repairs }: { repairs: string[] }) {
  const title =
    'This text contained encoding artifacts (' +
    repairs.join(', ') +
    ') that were auto-corrected for display. The backend applies the same ' +
    'correction before sending, so what you see is what will be posted.'
  return (
    <span style={styles.artifactBadge} title={title}>
      <AlertTriangle style={styles.iconSm} aria-hidden="true" />
      Auto-corrected encoding
    </span>
  )
}

// ---------------------------------------------------------------------------
// Inline scalar
// ---------------------------------------------------------------------------

export function InlineString({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <span style={styles.muted}>—</span>
  }
  if (typeof value === 'object') {
    let json: string
    try {
      json = JSON.stringify(value)
    } catch {
      json = String(value)
    }
    return <code style={styles.inlineCode}>{json}</code>
  }
  const { text, repaired, repairs } = normalizeArtifacts(String(value))
  if (repaired) {
    return (
      <span style={styles.artifactWrap}>
        <span>{text}</span>
        <ArtifactBadge repairs={repairs} />
      </span>
    )
  }
  return <span>{text}</span>
}

// ---------------------------------------------------------------------------
// Email chip
// ---------------------------------------------------------------------------

export function EmailChip({ email }: { email: string }) {
  const trimmed = email.trim()
  return (
    <a
      href={`mailto:${trimmed}`}
      style={styles.emailChip}
      title={`Send email to ${trimmed}`}
    >
      <Mail style={styles.iconSm} aria-hidden="true" />
      <span>{trimmed}</span>
    </a>
  )
}

// ---------------------------------------------------------------------------
// ID badge
// ---------------------------------------------------------------------------

export function IdBadge({ id }: { id: string }) {
  const [copied, setCopied] = useState(false)
  // Truncate from the end. Stream IDs are prefixed (e.g. `autoexec_`,
  // `cus_`, `eng_`) and the prefix is the disambiguating part, so a
  // mid-string ellipsis hid the only useful information.
  const display = id.length > 28 ? `${id.slice(0, 24)}…` : id

  const handleCopy = async (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    try {
      await navigator.clipboard.writeText(id)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1200)
    } catch {
      // Clipboard API can fail in test envs / permissionless contexts;
      // silently no-op rather than throw.
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      style={styles.idBadge}
      title={copied ? 'Copied' : `Copy ${id}`}
      aria-label={copied ? 'Copied' : `Copy ID ${id}`}
      className="approval-id-badge"
    >
      <Hash style={styles.iconXs} aria-hidden="true" />
      <span style={styles.idBadgeValue}>{display}</span>
      {copied ? (
        <Check style={styles.iconXs} aria-hidden="true" />
      ) : (
        <Copy style={styles.idBadgeCopyIcon} aria-hidden="true" />
      )}
    </button>
  )
}

// ---------------------------------------------------------------------------
// URL
// ---------------------------------------------------------------------------

export function UrlLink({ url }: { url: string }) {
  let label = url
  try {
    const parsed = new URL(url)
    label = parsed.host + parsed.pathname
    if (label.length > 60) label = label.slice(0, 57) + '…'
  } catch {
    // fall through with raw url
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={styles.url}>
      <LinkIcon style={styles.iconSm} aria-hidden="true" />
      <span>{label}</span>
    </a>
  )
}

// ---------------------------------------------------------------------------
// Currency
// ---------------------------------------------------------------------------

export function CurrencyValue({
  value,
  currency,
  isCents,
}: {
  value: number
  currency: string
  isCents?: boolean
}) {
  const amount = isCents ? value / 100 : value
  let formatted: string
  try {
    formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount)
  } catch {
    formatted = `${amount.toFixed(2)} ${currency.toUpperCase()}`
  }
  return <span style={styles.currency}>{formatted}</span>
}

// ---------------------------------------------------------------------------
// Relative time
// ---------------------------------------------------------------------------

export function RelativeTime({ value }: { value: string | number }) {
  let date: Date | null = null
  if (typeof value === 'number') {
    // Heuristic: small numbers are seconds since epoch, large are millis
    const ms = value < 1e12 ? value * 1000 : value
    date = new Date(ms)
  } else if (typeof value === 'string') {
    const parsed = new Date(value)
    if (!Number.isNaN(parsed.getTime())) date = parsed
  }
  if (!date || Number.isNaN(date.getTime())) {
    return <span>{String(value)}</span>
  }
  const relative = formatRelative(date)
  const absolute = date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return (
    <span style={styles.relativeTime} title={absolute}>
      {relative} <span style={styles.relativeTimeAbs}>· {absolute}</span>
    </span>
  )
}

function formatRelative(date: Date): string {
  const now = Date.now()
  const diff = now - date.getTime()
  const abs = Math.abs(diff)
  const sign = diff < 0 ? 'in ' : ''
  const suffix = diff < 0 ? '' : ' ago'

  const sec = Math.floor(abs / 1000)
  if (sec < 5) return 'just now'
  if (sec < 60) return `${sign}${sec}s${suffix}`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${sign}${min}m${suffix}`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${sign}${hr}h${suffix}`
  const day = Math.floor(hr / 24)
  if (day < 30) return `${sign}${day}d${suffix}`
  const month = Math.floor(day / 30)
  if (month < 12) return `${sign}${month}mo${suffix}`
  const year = Math.floor(day / 365)
  return `${sign}${year}y${suffix}`
}

// ---------------------------------------------------------------------------
// Boolean
// ---------------------------------------------------------------------------

export function Boolean({ value }: { value: boolean }) {
  return value ? (
    <span style={styles.boolTrue}>
      <Check style={styles.iconSm} aria-hidden="true" />
      Yes
    </span>
  ) : (
    <span style={styles.boolFalse}>
      <X style={styles.iconSm} aria-hidden="true" />
      No
    </span>
  )
}

// ---------------------------------------------------------------------------
// Multiline text
// ---------------------------------------------------------------------------

export function MultilineText({ text }: { text: string }) {
  // Registers with the card-level translate control; returns the original or
  // the English translation depending on whether the card is flipped.
  const shown = useCardTranslatedText(text)
  return <MultilineBody text={shown} />
}

function MultilineBody({ text }: { text: string }) {
  const { text: clean, repaired, repairs } = normalizeArtifacts(text)
  return (
    <div style={styles.artifactBlockWrap}>
      {repaired && <ArtifactBadge repairs={repairs} />}
      <div style={styles.multiline}>{clean}</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Markdown (lightweight inline impl, no extra deps)
// ---------------------------------------------------------------------------

export function Markdown({ text }: { text: string }) {
  // Markdown source is plain text, so it's safe to translate directly; the
  // card-level control decides whether to show the original or the translation.
  const shown = useCardTranslatedText(text)
  return <MarkdownBody text={shown} />
}

function MarkdownBody({ text }: { text: string }) {
  // The codebase ships a fuller MarkdownRenderer at
  // ./MarkdownRenderer.tsx but we keep this lightweight to avoid the
  // lazy-load roundtrip for inline use. Renders newlines + paragraphs;
  // **bold**, *italic*, `code`, and [text](url) inline.
  const { text: clean, repaired, repairs } = normalizeArtifacts(text)
  const html = renderMarkdownLite(clean)
  return (
    <div style={styles.artifactBlockWrap}>
      {repaired && <ArtifactBadge repairs={repairs} />}
      <div
        style={styles.markdown}
        // The renderMarkdownLite output is escaped before inserting markup
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  )
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function renderMarkdownLite(input: string): string {
  // Escape first, then apply minimal inline transforms on the escaped string.
  let out = escapeHtml(input)

  // ```code blocks```
  out = out.replace(/```([\s\S]*?)```/g, (_, code: string) => `<pre><code>${code}</code></pre>`)
  // `inline code`
  out = out.replace(/`([^`]+)`/g, '<code>$1</code>')
  // **bold**
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  // *italic*
  out = out.replace(/(^|\W)\*([^*]+)\*(\W|$)/g, '$1<em>$2</em>$3')
  // [text](url) — only allow http/https in href
  out = out.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
  // # through ###### headings — use styled divs rather than h4-h6 elements.
  // Browser defaults for h4-h6 scale from 1em down to 0.67em; inside the
  // 13px markdown container that makes h6 ≈ 8.7px (unreadably small).
  // ### and deeper render as small uppercase section dividers matching the
  // card's existing field-label style (same uppercase treatment as CUSTOMER /
  // AGENT group headers). # / ## render as slightly larger bold headings.
  out = out.replace(/^(#{1,6})\s+(.+)$/gm, (_, hashes: string, text: string) => {
    const depth = (hashes as string).length
    if (depth >= 3) {
      // #### / ##### / ###### → small uppercase section divider
      return `<div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);margin-top:12px;margin-bottom:4px">${text}</div>`
    }
    if (depth === 2) {
      return `<div style="font-size:13px;font-weight:600;color:var(--text-primary);margin-top:10px;margin-bottom:2px">${text}</div>`
    }
    return `<div style="font-size:14px;font-weight:600;color:var(--text-primary);margin-top:8px;margin-bottom:4px">${text}</div>`
  })
  // - bullet
  out = out.replace(/^- (.+)$/gm, '<li>$1</li>')
  // wrap consecutive <li> in <ul>
  out = out.replace(/(?:<li>.*<\/li>(?:\n|$))+/g, (m) => `<ul>${m}</ul>`)
  // Paragraph breaks: double newline → </p><p>
  out = '<p>' + out.replace(/\n{2,}/g, '</p><p>') + '</p>'
  // Single newline → <br/>
  out = out.replace(/\n/g, '<br/>')
  // Tidy: empty <p>
  out = out.replace(/<p>\s*<\/p>/g, '')
  return out
}

// ---------------------------------------------------------------------------
// Code block
// ---------------------------------------------------------------------------

export function CodeBlock({ value }: { value: unknown }) {
  let text: string
  if (typeof value === 'string') {
    text = value
  } else {
    try {
      text = JSON.stringify(value, null, 2)
    } catch {
      text = String(value)
    }
  }
  return <pre style={styles.codeBlock}>{text}</pre>
}

// ---------------------------------------------------------------------------
// Chip list
// ---------------------------------------------------------------------------

export function ChipList({ items }: { items: unknown[] }) {
  return (
    <span style={styles.chipList}>
      {items.map((item, idx) => (
        <span key={idx} style={styles.chip}>
          {String(item)}
        </span>
      ))}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Region chip (App Store territory — Task 4074c)
// ---------------------------------------------------------------------------

/**
 * Compact store-region chip for an App Store review's territory code.
 *
 * Surfaces which App Store a review came from (e.g. "US", "CA", "GB") next to
 * the rating in the `original_review` card section so operators can see the
 * store region without a third-party tool. The raw ASC territory code is shown
 * (uppercased + trimmed) — the codebase has no territory-code → label mapping
 * helper, and the reviews inspector likewise shows the raw code, so this stays
 * consistent. Renders nothing when the code is empty/blank (graceful absence).
 */
export function RegionChip({ code }: { code: string }) {
  const trimmed = code.trim().toUpperCase()
  if (trimmed.length === 0) return null
  return (
    <span style={styles.regionChip} title={`App Store region: ${trimmed}`}>
      <MapPin style={styles.iconSm} aria-hidden="true" />
      <span>{trimmed}</span>
    </span>
  )
}

// ---------------------------------------------------------------------------
// Diff block (used for previous_attributes vs current)
// ---------------------------------------------------------------------------

export interface DiffBlockProps {
  previous: Record<string, unknown>
  current: Record<string, unknown>
}

export function DiffBlock({ previous, current }: DiffBlockProps) {
  const keys = Array.from(new Set([...Object.keys(previous), ...Object.keys(current)]))
  if (keys.length === 0) {
    return <span style={styles.muted}>—</span>
  }
  return (
    <table style={styles.diffTable}>
      <thead>
        <tr>
          <th style={styles.diffHeaderCell}>Field</th>
          <th style={styles.diffHeaderCellRemoved}>Previous</th>
          <th style={styles.diffHeaderCellAdded}>Current</th>
        </tr>
      </thead>
      <tbody>
        {keys.map((k) => {
          const prev = previous[k]
          const next = current[k]
          const changed = !Object.is(stableStr(prev), stableStr(next))
          return (
            <tr key={k} style={changed ? styles.diffRowChanged : undefined}>
              <td style={styles.diffFieldCell}>{k}</td>
              <td style={styles.diffRemovedCell}>{stableStr(prev)}</td>
              <td style={styles.diffAddedCell}>{stableStr(next)}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function stableStr(v: unknown): string {
  if (v === undefined) return '—'
  if (v === null) return 'null'
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v)
    } catch {
      return String(v)
    }
  }
  return String(v)
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, CSSProperties> = {
  muted: {
    color: 'var(--text-muted)',
    fontStyle: 'italic',
  },
  inlineCode: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '12px',
    padding: '2px 6px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text-primary)',
  },
  emailChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--accent-secondary)',
    textDecoration: 'none',
    fontSize: '12px',
    fontFamily: "'JetBrains Mono', monospace",
  },
  idBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 6px 2px 6px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-sm)',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '11px',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    maxWidth: '100%',
    minWidth: 0,
    transition: 'background-color var(--transition-fast), border-color var(--transition-fast), color var(--transition-fast)',
  },
  idBadgeValue: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  idBadgeCopyIcon: {
    width: '10px',
    height: '10px',
    opacity: 0.45,
    flexShrink: 0,
  },
  url: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: 'var(--accent-secondary)',
    textDecoration: 'none',
    fontSize: '13px',
  },
  currency: {
    fontFamily: "'JetBrains Mono', monospace",
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  relativeTime: {
    fontSize: '13px',
  },
  relativeTimeAbs: {
    color: 'var(--text-muted)',
    fontSize: '12px',
  },
  boolTrue: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: 'var(--success)',
    fontSize: '13px',
    fontWeight: 500,
  },
  boolFalse: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: 'var(--text-muted)',
    fontSize: '13px',
  },
  multiline: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    padding: '10px 12px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
    fontSize: '13px',
    lineHeight: 1.5,
    color: 'var(--text-primary)',
  },
  artifactBlockWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  artifactWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap',
  },
  artifactBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    alignSelf: 'flex-start',
    padding: '2px 8px',
    background: 'var(--warning-bg, rgba(234, 179, 8, 0.12))',
    border: '1px solid var(--warning-border, rgba(234, 179, 8, 0.4))',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--warning-fg, #b45309)',
    fontSize: '11px',
    fontWeight: 500,
    cursor: 'help',
  },
  markdown: {
    padding: '10px 12px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
    fontSize: '13px',
    lineHeight: 1.5,
    color: 'var(--text-primary)',
    // Cap prose width at an optimal reading length so evidence summaries
    // stay legible in wide dialogs without stretching to full modal width.
    maxWidth: '68ch',
  },
  codeBlock: {
    margin: 0,
    maxHeight: '320px',
    overflow: 'auto',
    padding: '10px 12px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '12px',
    color: 'var(--text-primary)',
    whiteSpace: 'pre',
  },
  chipList: {
    display: 'inline-flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  chip: {
    padding: '2px 8px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '11px',
    color: 'var(--text-secondary)',
  },
  regionChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.4px',
    color: 'var(--text-secondary)',
  },
  iconXs: {
    width: '10px',
    height: '10px',
  },
  iconSm: {
    width: '12px',
    height: '12px',
  },
  diffTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '12px',
  },
  diffHeaderCell: {
    textAlign: 'left',
    padding: '6px 8px',
    background: 'var(--bg-tertiary)',
    borderBottom: '1px solid var(--border-default)',
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  diffHeaderCellRemoved: {
    textAlign: 'left',
    padding: '6px 8px',
    background: 'var(--bg-tertiary)',
    borderBottom: '1px solid var(--border-default)',
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--error)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  diffHeaderCellAdded: {
    textAlign: 'left',
    padding: '6px 8px',
    background: 'var(--bg-tertiary)',
    borderBottom: '1px solid var(--border-default)',
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--success)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  diffFieldCell: {
    padding: '6px 8px',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '11px',
    color: 'var(--text-secondary)',
    borderBottom: '1px solid var(--border-subtle)',
    verticalAlign: 'top',
  },
  diffRemovedCell: {
    padding: '6px 8px',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '12px',
    color: 'var(--error)',
    background: 'rgba(239, 68, 68, 0.05)',
    borderBottom: '1px solid var(--border-subtle)',
    verticalAlign: 'top',
    wordBreak: 'break-word',
  },
  diffAddedCell: {
    padding: '6px 8px',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '12px',
    color: 'var(--success)',
    background: 'rgba(34, 197, 94, 0.05)',
    borderBottom: '1px solid var(--border-subtle)',
    verticalAlign: 'top',
    wordBreak: 'break-word',
  },
  diffRowChanged: {
    background: 'transparent',
  },
}
